import { TiTick } from "react-icons/ti";
import { MdOutlineHourglassEmpty } from "react-icons/md";
import { api } from "./api";
import { API_ROUTES } from "../routes/apiRoutes";

export const handleViewPDF = async (reportID, addToast, t, lang) => {
  if (!reportID) return false;
  try {
    const response = await api.get(API_ROUTES.PDF_DATA_WITH_PARAMS(reportID, lang), {
      baseURL: process.env.REACT_APP_API_URL,
      responseType: "blob",
      _userAction: true,
    });
    const contentType = response.headers["content-type"];

    if (contentType?.includes("application/json")) {
      const text = await response.data.text();
      const json = JSON.parse(text);
      const pdfUrl = json.data?.pdfUrl || json.pdfUrl;

      if (pdfUrl) {
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
        return true;
      }

      addToast(t("main.reports.pdfNotReady"), "warning");
      return false;
    }

    if (contentType?.includes("application/pdf")) {
      const url = window.URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      return true;
    }

    return false;
  } catch (error) {
    if (error.response?.status === 404) {
      addToast(t("main.reports.pdfNotReady"), "warning");
    } else {
      addToast(t("main.reports.pdfDownloadFailed"), "error");
    }
    return false;
  }
};

export const handleDownloadPDF = async (reportID, addToast, t, lang, filename) => {
  if (!reportID) return false;
  try {
    const response = await api.get(API_ROUTES.PDF_DATA_WITH_PARAMS(reportID, lang), {
      baseURL: process.env.REACT_APP_API_URL,
      responseType: "blob",
      _userAction: true,
    });
    const contentType = response.headers["content-type"];

    if (contentType?.includes("application/json")) {
      const text = await response.data.text();
      const json = JSON.parse(text);
      const pdfUrl = json.data?.downloadUrl || json.downloadUrl;

      if (pdfUrl) {
        const iframe = document.createElement("iframe");
        iframe.style.cssText = "display:none;width:0;height:0;border:none;position:absolute;";
        iframe.src = pdfUrl;
        document.body.appendChild(iframe);
        setTimeout(() => document.body.removeChild(iframe), 10000);
        return true;
      }

      addToast(t("main.reports.pdfNotReady"), "warning");
      return false;
    }

    if (contentType?.includes("application/pdf")) {
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || `report_${reportID}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    }

    return false;
  } catch (error) {
    if (error.response?.status === 404) {
      addToast(t("main.reports.pdfNotReady"), "warning");
    } else {
      addToast(t("main.reports.pdfDownloadFailed"), "error");
    }
    return false;
  }
};

export const handlePDFDownloadWithModal = async (
  reportID,
  setIsLoading,
  setModalMessage,
  setModalOpen,
  addToast,
  lang,
) => {
  if (!reportID) return;

  setIsLoading(true);
  try {
    const response = await api.get(API_ROUTES.PDF_DATA_WITH_PARAMS(reportID, lang), {
      baseURL: process.env.REACT_APP_API_URL,
      responseType: "blob",
      _userAction: true,
    });
    const contentType = response.headers["content-type"];

    if (contentType === "application/json") {
      const reader = new FileReader();
      reader.onload = () => {
        const jsonResponse = JSON.parse(reader.result);
        if (jsonResponse.success === true) {
          if (!jsonResponse.data) {
            setModalMessage(
              <div className="flex flex-col items-center gap-2">
                <MdOutlineHourglassEmpty className="text-yellow-500" size={30} />
                <span>
                  {lang === "da"
                    ? "PDF-generering er i gang. Du vil modtage en e-mail med linket til PDF'en, når den er klar."
                    : "PDF generation is in progress. You will receive an email with the link to the PDF when it's ready."}
                </span>
              </div>,
            );
            setModalOpen(true);
          } else {
            const pdfUrl = jsonResponse.data.pdfUrl;
            window.open(pdfUrl, "_blank", "noopener,noreferrer");
            setModalMessage(
              <div className="flex flex-col items-center gap-2">
                <TiTick className="text-white bg-secondaryColor rounded-full p-1" size={30} />
                <span>
                  {lang === "da"
                    ? "PDF åbnet i en ny fane!"
                    : "PDF opened in a new tab successfully!"}
                </span>
              </div>,
            );
            setModalOpen(true);
          }
        } else {
          addToast(lang === "da" ? "Der opstod en fejl." : "An error occurred.", "error");
        }
      };
      reader.readAsText(response.data);
    } else if (contentType === "application/pdf") {
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `report_${reportID}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setModalMessage(
        <div className="flex flex-col items-center gap-2">
          <TiTick
            className="text-white bg-secondaryColor rounded-full p-2 animate-pulse-scale"
            size={30}
          />
          <span>{lang === "da" ? "PDF downloadet!" : "PDF downloaded successfully!"}</span>
        </div>,
      );
      setModalOpen(true);
    }
  } catch (error) {
    if (error.response && error.response.status === 502) {
      setModalMessage(
        lang === "da"
          ? "PDF-genereringsanmodningen kunne ikke behandles. Prøv igen senere."
          : "PDF generation request couldn't be serviced. Please try again later.",
      );
      setModalOpen(true);
    } else {
      addToast(
        lang === "da"
          ? "Kunne ikke generere rapporten. Prøv igen."
          : "Failed to generate the report. Please try again.",
        "error",
      );
    }
  } finally {
    setIsLoading(false);
  }
};

export const handleGeneratePDF = async (reportID, addToast, t) => {
  if (!reportID) return false;
  try {
    await api.post(
      API_ROUTES.PDF_DATA_GENERATE,
      { overviewImageId: reportID },
      { baseURL: process.env.REACT_APP_API_URL },
    );
    addToast(t("main.reports.pdfGenerationStarted"), "info");
    return true;
  } catch (error) {
    addToast(t("main.reports.pdfGenerateFailed"), "error");
    return false;
  }
};

const POLL_INTERVAL = 5000; // 5 seconds
const POLL_MAX_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Polls GET /dronetjek/pdf-data/ until the PDF is ready or timeout.
 * Returns true if PDF became ready, false on timeout/failure.
 * Calls onReady() as soon as the PDF is available.
 */
export const pollPdfReady = (reportID, { onReady, onFailed, lang = "en" }) => {
  const startTime = Date.now();
  let cancelled = false;

  const poll = async () => {
    if (cancelled) return;
    if (Date.now() - startTime > POLL_MAX_DURATION) {
      onFailed?.();
      return;
    }
    try {
      const response = await api.get(API_ROUTES.PDF_DATA_WITH_PARAMS(reportID, lang), {
        baseURL: process.env.REACT_APP_API_URL,
      });
      const contentType = response.headers["content-type"];
      if (contentType?.includes("application/json")) {
        const data = response.data;
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        if (parsed.success && parsed.data?.pdfUrl) {
          onReady?.();
          return;
        }
      } else if (contentType?.includes("application/pdf")) {
        onReady?.();
        return;
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        // Unexpected error — stop polling
        onFailed?.();
        return;
      }
      // 404 = not ready yet, continue polling
    }
    if (!cancelled) {
      setTimeout(poll, POLL_INTERVAL);
    }
  };

  setTimeout(poll, POLL_INTERVAL);

  // Return cancel function for cleanup
  return () => {
    cancelled = true;
  };
};
